class VideoPlayerMedia extends HTMLElement {
  #abort = null;
  #userPaused = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const video = this.querySelector('video');
    if (!video) return;
    if (this.closest('video-hero-media')) this.#setupScrollPause(video, signal);
    this.#setupPlayButton(video, signal);
    this.#setupProgress(video, signal);
    this.#setupMuteButton(video, signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  playMuted() {
    if (this.#userPaused) return;
    const video = this.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.play().catch(() => {});
  }

  #setupScrollPause(video, signal) {
    const pauseOutsideViewport = () => {
      if (video.paused) return;
      const rect = video.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      if (!rect.height || midpoint < 0 || midpoint > window.innerHeight) video.pause();
    };
    window.addEventListener('scroll', pauseOutsideViewport, { passive: true, signal });
    window.addEventListener('resize', pauseOutsideViewport, { signal });
    video.addEventListener('play', pauseOutsideViewport, { signal });
  }

  #setupPlayButton(video, signal) {
    const btn = this.querySelector('[data-video-btn]');
    if (!btn) return;
    const pauseIcon = btn.querySelector('.video-player__icon--pause');
    const playIcon = btn.querySelector('.video-player__icon--play');
    const sync = () => {
      const paused = video.paused;
      pauseIcon.hidden = paused;
      playIcon.hidden = !paused;
      btn.setAttribute('aria-label', (paused ? btn.dataset.labelPlay : btn.dataset.labelPause) || 'Play');
      this.classList.toggle('is-paused', paused);
    };
    btn.addEventListener('click', () => {
      if (video.paused) {
        this.#userPaused = false;
        video.play();
      } else {
        this.#userPaused = true;
        video.pause();
      }
    }, { signal });
    video.addEventListener('play', sync, { signal });
    video.addEventListener('pause', sync, { signal });
    sync();
  }

  #setupProgress(video, signal) {
    const track = this.querySelector('[data-video-progress]');
    const fill = this.querySelector('.video-player__progress-fill');
    if (!track || !fill) return;
    video.addEventListener('timeupdate', () => {
      if (!video.duration) return;
      fill.style.width = `${(video.currentTime / video.duration) * 100}%`;
    }, { signal });
    track.addEventListener('click', (e) => {
      if (!video.duration) return;
      const rect = track.getBoundingClientRect();
      video.currentTime = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1) * video.duration;
    }, { signal });
  }

  #setupMuteButton(video, signal) {
    const btn = this.querySelector('[data-video-mute]');
    if (!btn) return;
    const mutedIcon = btn.querySelector('.video-player__mute-icon--muted');
    const unmutedIcon = btn.querySelector('.video-player__mute-icon--unmuted');
    const sync = () => {
      mutedIcon.hidden = !video.muted;
      unmutedIcon.hidden = video.muted;
      btn.setAttribute('aria-label', (video.muted ? btn.dataset.labelUnmute : btn.dataset.labelMute) || 'Mute');
    };
    btn.addEventListener('click', () => {
      video.muted = !video.muted;
    }, { signal });
    video.addEventListener('volumechange', sync, { signal });
    sync();
  }
}

if (!customElements.get('video-player-media')) {
  customElements.define('video-player-media', VideoPlayerMedia);
}
